import React from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
} from '@mui/material'
import { MoreVert } from '@mui/icons-material'

export interface MobileDataCardField {
  label: string
  value: React.ReactNode
  icon?: React.ReactNode
  variant?: 'default' | 'chip' | 'avatar' | 'currency' | 'date'
  chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  chipVariant?: 'filled' | 'outlined'
  fullWidth?: boolean
}

export interface MobileDataCardAction {
  label: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
}

export interface MobileDataCardProps {
  title: string
  subtitle?: string
  avatar?: React.ReactNode
  fields: MobileDataCardField[]
  actions?: MobileDataCardAction[]
  onClick?: () => void
  elevation?: number
}

export const MobileDataCard: React.FC<MobileDataCardProps> = ({
  title,
  subtitle,
  avatar,
  fields,
  actions = [],
  onClick,
  elevation = 1,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleActionClick = (action: MobileDataCardAction) => {
    handleMenuClose()
    action.onClick()
  }

  const renderFieldValue = (field: MobileDataCardField) => {
    switch (field.variant) {
      case 'chip':
        return (
          <Chip
            label={field.value}
            color={field.chipColor || 'default'}
            variant={field.chipVariant || 'filled'}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
        )
      
      case 'avatar':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {field.icon && (
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {field.icon}
              </Avatar>
            )}
            <Typography variant="body2" fontWeight={500}>
              {field.value}
            </Typography>
          </Box>
        )
      
      case 'currency':
        return (
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {field.value}
          </Typography>
        )
      
      case 'date':
        return (
          <Typography variant="body2" color="text.secondary">
            {field.value}
          </Typography>
        )
      
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {field.icon && (
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                {field.icon}
              </Box>
            )}
            <Typography variant="body2" fontWeight={500}>
              {field.value}
            </Typography>
          </Box>
        )
    }
  }

  return (
    <Card
      elevation={elevation}
      onClick={onClick}
      sx={{
        backgroundColor: 'surface',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'outlineVariant',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '&:hover': onClick ? {
          boxShadow: 3,
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
        } : {},
        '&:active': onClick ? {
          transform: 'translateY(0)',
        } : {},
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          {avatar && (
            <Box sx={{ mr: 2 }}>
              {avatar}
            </Box>
          )}
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                color: 'onSurface',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="onSurfaceVariant"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {actions.length > 0 && (
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                ml: 1,
                color: 'onSurfaceVariant',
                '&:hover': {
                  backgroundColor: 'surfaceVariant',
                },
              }}
            >
              <MoreVert />
            </IconButton>
          )}
        </Box>

        {/* Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {fields.map((field, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...(field.fullWidth && {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.5,
                }),
              }}
            >
              <Typography
                variant="caption"
                color="onSurfaceVariant"
                sx={{
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {field.label}
              </Typography>
              {renderFieldValue(field)}
            </Box>
          ))}
        </Box>
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            backgroundColor: 'surfaceContainer',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'outlineVariant',
            boxShadow: 3,
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        {actions.map((action, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider sx={{ my: 0.5 }} />}
            <MenuItem
              onClick={() => handleActionClick(action)}
              disabled={action.disabled}
              sx={{
                py: 1.5,
                px: 2,
                gap: 2,
                minHeight: 48,
                '&:hover': {
                  backgroundColor: 'surfaceVariant',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: action.color ? `${action.color}.main` : 'onSurface',
                  minWidth: 'auto',
                }}
              >
                {action.icon}
              </ListItemIcon>
              <ListItemText
                primary={action.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 500,
                  color: action.color ? `${action.color}.main` : 'onSurface',
                }}
              />
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </Card>
  )
}

export default MobileDataCard
